import { useState, useCallback } from 'react';
import { CasesApi } from '@/api';

interface UseFolderManagementOptions {
  caseId: string | undefined;
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export const useFolderManagement = ({
  caseId,
  onSuccess,
  onError,
}: UseFolderManagementOptions) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = useCallback(
    (err: any, defaultMessage: string) => {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        defaultMessage;
      setError(errorMessage);
      if (onError) onError(errorMessage);
    },
    [onError]
  );

  const createFolder = useCallback(
    async (name: string, parentId?: string) => {
      if (!caseId) {
        handleError(null, 'Case ID is required');
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await CasesApi.createFolder(caseId, name, parentId);

        if (response.success) {
          if (onSuccess) onSuccess();
          return response.folder;
        } else {
          handleError(response, 'Failed to create folder');
          return null;
        }
      } catch (err: any) {
        handleError(err, 'Failed to create folder');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [caseId, handleError, onSuccess]
  );

  const renameFolder = useCallback(
    async (folderId: string, newName: string) => {
      if (!caseId) {
        handleError(null, 'Case ID is required');
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await CasesApi.renameFolder(caseId, folderId, newName);

        if (response.success) {
          if (onSuccess) onSuccess();
          return response.folder;
        } else {
          handleError(response, 'Failed to rename folder');
          return null;
        }
      } catch (err: any) {
        handleError(err, 'Failed to rename folder');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [caseId, handleError, onSuccess]
  );

  const moveFolder = useCallback(
    async (folderId: string, newParentId?: string | null) => {
      if (!caseId) {
        handleError(null, 'Case ID is required');
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await CasesApi.moveFolder(caseId, folderId, newParentId);

        if (response.success) {
          if (onSuccess) onSuccess();
          return response.folder;
        } else {
          handleError(response, 'Failed to move folder');
          return null;
        }
      } catch (err: any) {
        handleError(err, 'Failed to move folder');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [caseId, handleError, onSuccess]
  );

  const deleteFolder = useCallback(
    async (folderId: string, options?: { cascade?: boolean; hard?: boolean }) => {
      if (!caseId) {
        handleError(null, 'Case ID is required');
        return false;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await CasesApi.deleteFolder(caseId, folderId, options);

        if (response.success) {
          if (onSuccess) onSuccess();
          return true;
        } else {
          handleError(response, 'Failed to delete folder');
          return false;
        }
      } catch (err: any) {
        handleError(err, 'Failed to delete folder');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [caseId, handleError, onSuccess]
  );

  const restoreFolder = useCallback(
    async (folderId: string) => {
      if (!caseId) {
        handleError(null, 'Case ID is required');
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await CasesApi.restoreFolder(caseId, folderId);

        if (response.success) {
          if (onSuccess) onSuccess();
          return response.folder;
        } else {
          handleError(response, 'Failed to restore folder');
          return null;
        }
      } catch (err: any) {
        handleError(err, 'Failed to restore folder');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [caseId, handleError, onSuccess]
  );

  const moveFile = useCallback(
    async (documentId: string, targetFolderId?: string | null) => {
      if (!caseId) {
        handleError(null, 'Case ID is required');
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await CasesApi.moveFile(caseId, documentId, targetFolderId);

        if (response.success) {
          if (onSuccess) onSuccess();
          return response.document;
        } else {
          handleError(response, 'Failed to move file');
          return null;
        }
      } catch (err: any) {
        handleError(err, 'Failed to move file');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [caseId, handleError, onSuccess]
  );

  return {
    loading,
    error,
    createFolder,
    renameFolder,
    moveFolder,
    deleteFolder,
    restoreFolder,
    moveFile,
  };
};

export default useFolderManagement;
